import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';

const YELLOW = '#F4C430';

const trimOrNull = (v) => {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
};

export const SupplierInfoModal = ({ open, onClose, onSaved, item }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && item) {
      setForm({
        supplierName:  item.supplierName ?? '',
        supplierUrl:   item.supplierUrl ?? '',
        brandName:     item.brandName ?? '',
        reorderQty:    item.reorderQty ?? '',
        lastReorderAt: item.lastReorderAt ?? '',
        batchNotes:    item.batchNotes ?? '',
      });
      setError(null);
    }
  }, [open, item]);

  if (!open || !item) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.currentTarget.value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.patch(`/inventory/${item.id}/supplier`, {
        supplierName:  trimOrNull(form.supplierName),
        supplierUrl:   trimOrNull(form.supplierUrl),
        brandName:     trimOrNull(form.brandName),
        reorderQty:    form.reorderQty === '' ? null : parseInt(form.reorderQty, 10),
        lastReorderAt: trimOrNull(form.lastReorderAt),
        batchNotes:    trimOrNull(form.batchNotes),
      });
      onSaved?.(result);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: YELLOW }}>Supplier info</p>
          <p class="m-0 font-serif text-lg font-medium text-text-primary">{item.name}</p>
        </div>

        <div class="space-y-4 p-5">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Supplier name</p>
              <input
                type="text"
                value={form.supplierName ?? ''}
                onInput={set('supplierName')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Brand name</p>
              <input
                type="text"
                value={form.brandName ?? ''}
                onInput={set('brandName')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Supplier URL</p>
            <input
              type="text"
              value={form.supplierUrl ?? ''}
              onInput={set('supplierUrl')}
              placeholder="https://…"
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Reorder qty</p>
              <input
                type="number"
                min={0}
                value={form.reorderQty ?? ''}
                onInput={set('reorderQty')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Last reorder</p>
              <input
                type="text"
                placeholder="YYYY-MM-DD"
                value={form.lastReorderAt ?? ''}
                onInput={set('lastReorderAt')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Batch / lot notes</p>
            <textarea
              rows={3}
              value={form.batchNotes ?? ''}
              onInput={set('batchNotes')}
              placeholder="Batch IDs, expiry dates, anything supplier-related to remember…"
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
            </p>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
};
